/**
 * stage.js
 *
 * computeStage(contact, demos, trials, outreachLog) → stage string
 *
 * Stages (spec v5 logic — first match wins):
 *   'customer'        — trial status = Converted
 *   'dead'            — trial status = Churned (all trials dead), or demo next_step = Dead
 *   'trial'           — active trial row (status = Active)
 *   'demo'            — scheduled demo OR completed demo where next_step ≠ Dead
 *   'warm'            — any inbound reply, or any opened/clicked email in outreach log
 *   'cold'            — at least one outreach row but no warm signal
 *   'cold-untouched'  — no outreach ever
 *
 * All values computed fresh from sheet data — never stored in the Contacts sheet.
 * Matching is always by contact_id (not email), matching actual sheet schema.
 */

export function computeStage(contact, demos = [], trials = [], outreachLog = []) {
  // Manual override always wins (set by StagePicker or auto-promoted by gmail-sync)
  if (contact.stage_override) return contact.stage_override;

  const contactId = contact.contact_id ?? '';

  // Filter related rows upfront
  const cTrials    = trials.filter((t) => t.contact_id === contactId);
  const cDemos     = demos.filter((d) => d.contact_id === contactId);
  const cOutreach  = outreachLog.filter((r) => r.contact_id === contactId);

  // 1. Active trial → Trial
  if (cTrials.some((t) => (t.status ?? '').toLowerCase() === 'active')) {
    return 'trial';
  }

  // 2. Converted trial → Customer
  if (cTrials.some((t) => (t.status ?? '').toLowerCase() === 'converted')) {
    return 'customer';
  }

  // 3. All trials churned → Dead  (only when at least one trial exists)
  if (cTrials.length > 0 && cTrials.every((t) => (t.status ?? '').toLowerCase() === 'churned')) {
    return 'dead';
  }

  // 4. Demo: scheduled OR completed with next_step ≠ Dead
  const activeDemo = cDemos.find((d) => {
    const dStatus  = (d.status   ?? '').toLowerCase();
    const nextStep = (d.next_step ?? '').toLowerCase();
    if (dStatus === 'scheduled') return true;
    if (dStatus === 'completed' && nextStep !== 'dead') return true;
    return false;
  });
  if (activeDemo) return 'demo';

  // 5. Warm — reply classified as need_followup (matches backend /api/contacts SQL)
  const hasFollowup = cOutreach.some((r) => (r.reply_type ?? '') === 'need_followup');
  if (hasFollowup) return 'warm';

  // 6. Cold — at least one outbound touch logged
  if (cOutreach.length > 0) return 'cold';

  // 7. Cold-untouched
  return 'cold-untouched';
}

// Canonical contact-stage enum (IMP-1833) — mirrors backend/lib/stages.js,
// which documents the stage ↔ trial-status ↔ Clerk-lifecycle mapping.
// 'deleted' is only ever written by the offboard flow (never by StagePicker);
// the backend hides deleted contacts from every listing, count and export.
export const CONTACT_STAGES = [
  'cold-untouched', 'cold', 'warm', 'demo', 'trial', 'customer', 'user', 'dead', 'deleted',
];

// The stages a user can pick or filter by — everything except 'deleted'
// (deleted is set only by the offboard flow, and deleted contacts never
// appear in listings to be filtered). Ordered hottest-first for display.
export const PICKABLE_STAGES = ['customer', 'trial', 'user', 'demo', 'warm', 'cold', 'dead', 'cold-untouched'];

// Human-readable label for a stage string
export const STAGE_LABELS = {
  'customer':       'Customer',
  'dead':           'Dead',
  'trial':          'Trial',
  'user':           'User',
  'demo':           'Demo',
  'warm':           'Warm',
  'cold':           'Cold',
  'cold-untouched': 'Cold / Untouched',
  'deleted':        'Deleted',
};

// CSS class suffix for badge styling
export const STAGE_BADGE_CLASS = {
  'customer':       'badge-customer',
  'dead':           'badge-dead',
  'trial':          'badge-trial',
  'user':           'badge-user',
  'demo':           'badge-demo',
  'warm':           'badge-warm',
  'cold':           'badge-cold',
  'cold-untouched': 'badge-cold-untouched',
  'deleted':        'badge-dead',
};
